import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Film, Calendar, Users, Sparkles, Shield, Globe, BookOpen, Play, Target, Zap, Award } from 'lucide-react';
import Footer from '../../components/landing/Footer';
import { useNavigate } from 'react-router-dom';

const MediaEventsOverview = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br via-white to-orange-50 from-slate-50">
      {/* Back Button */}
      <div className="fixed left-4 top-24 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
          className="flex gap-2 items-center px-4 py-2.5 rounded-xl border border-gray-200 shadow-lg backdrop-blur-md transition-all duration-300 bg-white/95 hover:bg-white hover:shadow-2xl"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">Back</span>
        </motion.button>
      </div>

      {/* Hero Section with Background Image */}
      <section className="overflow-hidden relative h-[600px] pt-20 md:pt-24 lg:pt-28 pb-20 md:pb-24 lg:pb-28 mt-16 md:mt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop"
            alt="Conference Hall"
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/90 via-blue-900/85 to-purple-900/90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(251,146,60,0.3),transparent_50%)]" />
        </div>
        
        {/* Animated particles */}
        <div className="overflow-hidden absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full opacity-20"
              animate={{
                y: [0, -1000],
                x: [0, Math.random() * 100 - 50],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: '100%',
              }}
            />
          ))}
        </div>
        
        <div className="flex relative z-10 justify-center items-center px-6 py-12 mx-auto max-w-6xl h-full sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full text-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex gap-2 items-center px-6 py-3 mb-8 rounded-full border shadow-xl backdrop-blur-md md:mb-10 bg-white/20 border-white/30"
            >
              <Sparkles className="w-5 h-5 text-orange-300" />
              <span className="text-sm font-bold tracking-wide text-white uppercase">TripSecure Media & Events</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-10 text-5xl font-black leading-tight text-white md:mb-12 md:text-6xl lg:text-7xl"
            >
              Educate. Engage.
              <span className="block mt-3 text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-yellow-300 to-orange-400 md:mt-4">
                Secure.
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mx-auto mb-12 max-w-3xl text-xl leading-relaxed text-gray-100 md:mb-14 md:text-2xl"
            >
              The storytelling and engagement arm of TripSecure Ghana Ltd
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex gap-4 justify-center mt-6"
            >
              <motion.button
                onClick={()=>navigate("/media-events")}
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(251,146,60,0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="flex gap-2 items-center px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-2xl transition-all hover:from-orange-600 hover:to-orange-700"
              >
                <Play className="w-5 h-5" />
                Explore Events
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/50">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="mx-auto mt-2 w-1 h-3 bg-white rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="px-4 py-20 mx-auto -mt-32 max-w-7xl sm:px-6">
        <motion.div
          {...fadeInUp}
          className="relative p-10 bg-white rounded-3xl border border-gray-100 shadow-2xl lg:p-16"
        >
          <div className="flex absolute -top-8 left-10 justify-center items-center w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-xl">
            <Target className="w-8 h-8 text-white" />
          </div>
          <div className="max-w-none prose prose-lg">
            <p className="text-xl leading-relaxed text-gray-700 md:text-2xl">
              TripSecure Media & Events is the storytelling and engagement arm of TripSecure Ghana Ltd, designed to educate, influence, and activate audiences around travel safety, insurance literacy, mobility, tourism, and public risk awareness.
            </p>
            <p className="mt-6 text-lg leading-relaxed text-gray-600">
              We use media, live events, digital content, and strategic partnerships to simplify complex topics such as insurance, travel risks, road safety, and emergency preparedness—turning them into relatable, actionable knowledge for everyday people.
            </p>
          </div>
        </motion.div>
      </section>

      {/* What We Do Section */}
      <section className="px-4 py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="mx-auto max-w-7xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-black text-gray-900 md:text-5xl">What We Do</h2>
            <div className="mx-auto w-24 h-1.5 bg-gradient-to-r from-orange-500 to-blue-600 rounded-full" />
          </motion.div>
          
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Media Production */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="overflow-hidden relative p-8 h-full bg-white rounded-3xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-2xl lg:p-10">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600" />
                </div>
                
                <div className="relative">
                  {/* Image */}
                  <div className="overflow-hidden mb-6 rounded-2xl shadow-lg">
                    <img 
                      src="https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?q=80&w=2069&auto=format&fit=crop"
                      alt="Media Production"
                      className="object-cover w-full h-48 transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  <div className="flex gap-4 items-center mb-6">
                    <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg">
                      <Film className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-900">Media Production</h3>
                  </div>
                  
                  <ul className="space-y-4">
                    {[
                      "Educational podcasts and video shows (e.g. Insurance Under 1 Minute)",
                      "Documentary-style content on travel, transport, safety, and tourism",
                      "Branded media for corporate and institutional partners",
                      "Influencer-led campaigns and digital storytelling"
                    ].map((item, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex gap-3 items-start"
                      >
                        <Zap className="flex-shrink-0 mt-1 w-5 h-5 text-orange-500" />
                        <span className="text-base leading-relaxed text-gray-700">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Events & Experiences */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative group"
            >
              <div className="overflow-hidden relative p-8 h-full bg-white rounded-3xl border border-gray-100 shadow-lg transition-all duration-300 hover:shadow-2xl lg:p-10">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600" />
                </div>
                
                <div className="relative">
                  {/* Image */}
                  <div className="overflow-hidden mb-6 rounded-2xl shadow-lg">
                    <img 
                      src="https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=2069&auto=format&fit=crop"
                      alt="Events & Experiences"
                      className="object-cover w-full h-48 transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  <div className="flex gap-4 items-center mb-6">
                    <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg">
                      <Calendar className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-900">Events & Experiences</h3>
                  </div>
                  
                  <ul className="space-y-4">
                    {[
                      "TripSecure Expo – Africa's leading platform on insurance, safety, travel & tourism",
                      "National senior high school drama festival",
                      "Road safety campaigns and community outreach programmes",
                      "School, youth, and transport-sector engagements",
                      "Conferences, exhibitions, and branded activations"
                    ].map((item, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex gap-3 items-start"
                      >
                        <Award className="flex-shrink-0 mt-1 w-5 h-5 text-blue-500" />
                        <span className="text-base leading-relaxed text-gray-700">{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Partnerships */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-7xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden relative p-10 bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 rounded-3xl border border-emerald-100 shadow-xl lg:p-16"
          >
            {/* Decorative elements */}
            <div className="flex absolute -top-6 -right-6 justify-center items-center w-32 h-32 bg-gradient-to-br rounded-full blur-2xl from-emerald-400/20 to-teal-400/20" />
            <div className="flex absolute -bottom-6 -left-6 justify-center items-center w-32 h-32 bg-gradient-to-br rounded-full blur-2xl from-blue-400/20 to-emerald-400/20" />
            
            <div className="relative">
              <div className="flex gap-4 items-center mb-8">
                <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-xl">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-4xl font-black text-gray-900">Strategic Partnerships</h3>
                  <p className="mt-1 text-lg text-emerald-700">We work with:</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  { icon: Shield, text: "Insurance providers", color: "from-blue-500 to-blue-600" },
                  { icon: Globe, text: "Transport unions and operators", color: "from-emerald-500 to-emerald-600" },
                  { icon: Sparkles, text: "Travel & tourism brands", color: "from-purple-500 to-purple-600" },
                  { icon: Users, text: "Development partners and NGOs", color: "from-orange-500 to-orange-600" },
                  { icon: BookOpen, text: "Government agencies and regulators", color: "from-teal-500 to-teal-600" },
                ].map((partner, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    whileHover={{ y: -5, scale: 1.03 }}
                    className="flex gap-3 items-center p-5 bg-white rounded-2xl border border-gray-200 shadow-md transition-all hover:shadow-xl"
                  >
                    <div className={`flex justify-center items-center w-12 h-12 bg-gradient-to-br ${partner.color} rounded-xl shadow-lg`}>
                      <partner.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-semibold text-gray-800">{partner.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement - Enhanced */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="overflow-hidden relative p-12 text-center rounded-3xl shadow-2xl lg:p-20"
            style={{
              background: "linear-gradient(135deg, #f97316 0%, #ea580c 25%, #dc2626 50%, #9333ea 75%, #3b82f6 100%)",
            }}
          >
            {/* Animated background shapes */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
              }}
              transition={{ duration: 20, repeat: Infinity }}
              className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full opacity-10 blur-3xl"
            />
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                rotate: [0, -90, 0],
              }}
              transition={{ duration: 25, repeat: Infinity }}
              className="absolute bottom-0 left-0 w-80 h-80 bg-white rounded-full opacity-10 blur-3xl"
            />
            
            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex justify-center items-center mx-auto mb-8 w-20 h-20 rounded-full border-4 shadow-xl backdrop-blur-md bg-white/20 border-white/30"
              >
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mb-8 text-2xl font-semibold leading-relaxed text-white md:text-3xl"
              >
                Our media and events are designed not just to inform—but to convert audiences into safer travellers, informed consumers, and TripSecure users or agents.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="pt-8 mt-8 border-t border-white/30"
              >
                <p className="text-4xl font-black text-white md:text-5xl lg:text-6xl">
                  TripSecure Media & Events:
                </p>
                <p className="mt-4 text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-orange-200 to-yellow-200 md:text-4xl lg:text-5xl">
                  Educate. Engage. Secure.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MediaEventsOverview;
